/**
 * useSelection — a selection SET for spatial surfaces.
 *
 * Generalised from TaskBubbleCanvas's `Record<id, bool>` + bulk-action-bar
 * model, which was the only mature multi-select in the product and was welded
 * to the task domain (CANVAS-AUTHORING-ADVERSARIAL-REVIEW-2026-09 §B2).
 *
 * Nothing here knows what an object is — ids in, ids out — so the goal board,
 * the org chart and the bubble canvas can share one selection idiom.
 *
 * `pruneTo` is the part that is easy to forget and always needed: objects
 * disappear underneath a selection (deleted, paused off the board, filtered
 * away) and a selection holding dead ids silently applies bulk actions to
 * nothing.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
export function useSelection() {
    const [selected, setSelected] = useState(() => new Set());
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const select = useCallback((id, opts = {}) => {
        setSelected((cur) => {
            if (!opts.additive) {
                if (cur.size === 1 && cur.has(id))
                    return cur;
                return new Set([id]);
            }
            const next = new Set(cur);
            if (opts.toggle && next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    const setSelection = useCallback((ids) => {
        setSelected(new Set(ids));
    }, []);
    const addMany = useCallback((ids) => {
        setSelected((cur) => {
            const next = new Set(cur);
            for (const id of ids)
                next.add(id);
            return next;
        });
    }, []);
    const toggle = useCallback((id) => select(id, { additive: true, toggle: true }), [select]);
    const clear = useCallback(() => {
        setSelected((cur) => (cur.size ? new Set() : cur));
    }, []);
    const pruneTo = useCallback((live) => {
        const liveSet = live instanceof Set ? live : new Set(live);
        setSelected((cur) => {
            if (!cur.size)
                return cur;
            let changed = false;
            const next = new Set();
            for (const id of cur) {
                if (liveSet.has(id))
                    next.add(id);
                else
                    changed = true;
            }
            return changed ? next : cur;
        });
    }, []);
    const ids = useMemo(() => [...selected], [selected]);
    return {
        selected,
        ids,
        size: selected.size,
        isSelected: useCallback((id) => selectedRef.current.has(id), []),
        only: selected.size === 1 ? ids[0] : null,
        select,
        setSelection,
        addMany,
        toggle,
        clear,
        pruneTo,
        selectedRef,
    };
}
export default useSelection;
